import MapPlaceLayers from './MapPlaceLayers';
import {mapPlaceCommands} from '../services/mapPlaces';
import useDiscoveryDialog from '../hooks/useDiscoveryDialog';
import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import L from 'leaflet';
import { addIllustratedBasemap } from '../services/illustratedBasemap';
import { assistantRequest } from '../services/assistantApi';
import { mapCoordinates, zoomMap } from '../services/assistantNavigation';
import { homeAddress } from '../services/assistantDiscovery';
import './AssistantDiscovery.css';
export default function NearbyPlaces({
  listingKey,
  home,
  controlRef,
  onState,
  onResult
}) {
  const [open, setOpen] = useState(false),
    [data, setData] = useState(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  const [destinationChoices,setDestinationChoices]=useState([]),[selectedDestination,setSelectedDestination]=useState(null),[destinationEditing,setDestinationEditing]=useState(false),[suggesting,setSuggesting]=useState(false),[suggestionError,setSuggestionError]=useState('');
  const [destination, setDestination] = useState(''),
    [mode, setMode] = useState('drive');
  const lookupCache=useRef(new Map()),quota=useRef(null);
  async function requestDiscovery(kind,body,options){
    const key=JSON.stringify([kind,body]),cached=lookupCache.current.get(key);
    if(cached && cached.expires>Date.now())return cached.data;
    if(quota.current?.until>Date.now())throw quota.current.error;
    try{
      const result=await (options ? assistantRequest('/'+kind,body,options) : assistantRequest('/'+kind,body));
      if(lookupCache.current.size>=50)lookupCache.current.delete(lookupCache.current.keys().next().value);
      lookupCache.current.set(key,{data:result,expires:Date.now()+10*60*1000});
      return result;
    }catch(e){
      if(e.status===429){
        const tomorrow=new Date();tomorrow.setUTCHours(24,0,0,0);
        quota.current={error:e,until:e.retryAt || tomorrow.getTime()};
      }
      throw e;
    }
  }
  const mapInstance = useRef(null),placeLayersRef=useRef(null);
  const [liveMap,setLiveMap]=useState(null);
  const [mapZoom, setMapZoom] = useState(null);
  const mapElement = useRef(null),
    generation = useRef(0),
    requestBusy = useRef(false),
    stateRef = useRef({});
  stateRef.current = {
    open,
    data,
    destination,
    mode, selectedDestination, destinationChoices
  };
  const close = () => {
    generation.current++;
    requestBusy.current = false;
    setBusy(false);
    setOpen(false);
    setDestinationEditing(false);
    setDestinationChoices([]);
    return 'Back to the property details.';
  };
  useEffect(() => {
    const requestVersion = generation;
    generation.current++;
    setOpen(false);
    setData(null);
    setError('');
    setDestination('');
    setSelectedDestination(null);setDestinationChoices([]);setDestinationEditing(false);
    requestBusy.current = false;
    setBusy(false);
    return () => {
      requestVersion.current++;
    };
  }, [listingKey]);
  useEffect(()=>{
    if(!open || !destinationEditing || destination.trim().length<3){setSuggesting(false);setDestinationChoices([]);return undefined;}
    const controller=new AbortController();let active=true;
    setDestinationChoices([]);setSuggestionError('');setSuggesting(true);
    const timer=setTimeout(async()=>{
      try{
        // Without destination_id this existing endpoint only resolves address choices.
        const result=await requestDiscovery('route',{listing_key:listingKey,destination:destination.trim(),mode},{signal:controller.signal});
        if(active)setDestinationChoices(result.choices || (result.destination?[result.destination]:[]));
      }catch(e){if(active && e.name!=='AbortError')setSuggestionError(e.message);}
      finally{if(active)setSuggesting(false);}
    },800);
    return()=>{active=false;clearTimeout(timer);controller.abort();};
  // Cache and quota refs are shared across lookups; requests depend on these inputs.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[open,destinationEditing,destination,mode,listingKey]);
  const chooseDestination=choice=>{setDestination(choice.address);setSelectedDestination(choice);setDestinationEditing(false);setDestinationChoices([]);setSuggestionError('');setError('');};
  const performLookup=async(kind,params)=>{const result=await lookup(kind,params);onResult?.(result);};
  const mapHome=data?.home || home;
  const initialLayer=useMemo(()=>data?.places?{...data,scope:data.scope || 'Within about 5 miles of this home'}:null,[data]);
  const summary = data?.places ? data.places.map((p, i) => `${i + 1}. ${p.name}: ${p.address}`).join('\n') : data?.choices ? data.choices.map((p, i) => `${i + 1}. ${p.address}`).join('\n') : data?.seconds != null ? `${data.mode}: approximately ${Math.round(data.seconds / 60)} minutes to ${data.destination?.address}; ${(data.meters / 1609.344).toFixed(1)} miles. ${data.note}` : '';
  useEffect(() => {
    onState?.({
      nearby_open: open, nearby_map_ready: open && !!mapCoordinates(mapHome) && mapZoom != null, nearby_map_zoom: open && mapHome ? mapZoom : null,
      nearby_summary: open ? `${summary}\n${data?.note || ''}` : ''
    });
  }, [open, summary, data?.note, mapHome, mapZoom, onState]);
  async function lookup(kind, params = {}) {
    if (requestBusy.current) return 'A map lookup is already running.';
    requestBusy.current = true;
    setOpen(true);
    setBusy(true);
    setError('');
    const version = ++generation.current;
    try {
      let body = {
        listing_key: listingKey
      };
      if (kind === 'nearby') body.category = params.category || 'parks';else {
        const current = stateRef.current;
        body = {
          ...body,
          destination: params.destination || params.choice?.address || current.destination,
          mode: params.mode || current.mode
        };
        const choice = params.choice || (params.number != null ? (current.data?.choices || current.destinationChoices)?.[params.number - 1] : !params.destination ? current.selectedDestination : null);
        if (params.number != null && !choice) {const message='Choose one of the destination numbers shown on the screen.';setError(message);return message;}
        if (choice) {body.destination_id = choice.id;body.destination=choice.address;setSelectedDestination(choice);}else setSelectedDestination(null);
        if (body.destination.trim().length<3) {const message='Enter an address or destination with at least three characters.';setError(message);return message;}
        setDestinationEditing(false);setDestinationChoices([]);setSuggestionError('');
        setDestination(body.destination);
        setMode(body.mode);
      }
      let next = await requestDiscovery(kind, body);
      // Rural homes can have no venues within five miles. Try a bounded wider
      // area once, rather than leaving the quick category with an unexplained blank map.
      const center=mapCoordinates(next.home);
      if(kind==='nearby' && next.places?.length===0 && center && version===generation.current){
        const latitudeSpan=16/111.2;
        const longitudeSpan=16/(111.2*Math.max(.01,Math.cos(center.lat*Math.PI/180)));
        if(center.lat-latitudeSpan>=-90 && center.lat+latitudeSpan<=90 && center.lon-longitudeSpan>=-180 && center.lon+longitudeSpan<=180){
          const wider=await requestDiscovery('map-places',{category:body.category,bounds:{north:center.lat+latitudeSpan,south:center.lat-latitudeSpan,east:center.lon+longitudeSpan,west:center.lon-longitudeSpan}});
          next={...wider,home:next.home,scope:'In the surrounding area (up to 10 miles in each direction)'};
        }
      }
      if (version !== generation.current) return 'The map view changed.';
      // Reapply a cached category too: its layer may have been removed on the map.
      setData({...next});
      if(next.destination?.address){setDestination(next.destination.address);setSelectedDestination(next.destination);}
      if (next.places) return next.places.length ? `I found ${next.places.length} nearby ${next.category}: ${next.places.map(p => p.name).join(', ')}. ${next.note}` : 'No matching places were returned for this area.';
      if (next.choices) return 'Which destination do you mean? ' + next.choices.map((p, i) => `${i + 1}: ${p.address}`).join('. ');
      return `Approximately ${Math.round(next.seconds / 60)} minutes ${next.mode === 'walk' ? 'walking' : 'driving'}, ${(next.meters / 1609.344).toFixed(1)} miles to ${next.destination.address}. ${next.note}`;
    } catch (e) {
      if (version === generation.current) setError(e.message);
      return e.message;
    } finally {
      if (version === generation.current) {
        requestBusy.current = false;
        setBusy(false);
      }
    }
  }
  const dialogRef=useDiscoveryDialog(open,close);
  useImperativeHandle(controlRef, () => ({
    run: async params => {
      if(mapPlaceCommands.includes(params.command))return placeLayersRef.current?.run(params,params.isCurrent) || 'The map is loading.';
      if (['zoom_map_in','zoom_map_out'].includes(params.command)) return zoomMap(mapInstance.current, params.command === 'zoom_map_in' ? 1 : -1);
      if (params.command === 'close_nearby') return close();
      if (params.command === 'open_nearby') {
        setOpen(true);
        return 'Nearby places and travel times are open. Choose a category, or tell me a destination.';
      }
      return lookup(params.command === 'route_to' ? 'route' : 'nearby', params);
    }
  }));
  useEffect(() => {
    if (!open || !mapCoordinates(mapHome) || !mapElement.current) return undefined;
    const {lat,lon}=mapCoordinates(mapHome);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return undefined;
    const map = L.map(mapElement.current, {zoomAnimation:false,fadeAnimation:false,markerZoomAnimation:false,minZoom:1,maxZoom:19}).setView([lat, lon], 14);
    addIllustratedBasemap(map);
    map.attributionControl?.addAttribution('<a href="https://www.geoapify.com/">Geoapify</a>');
    const bounds = [[lat, lon]];
    L.circleMarker([lat, lon], {
      radius: 10,
      color: '#124f49',
      fillOpacity: 1
    }).addTo(map).bindTooltip('Selected home');
    (data?.places || []).forEach((p, i) => {
      const point = [Number(p.latitude), Number(p.longitude)];
      if (!point.every(Number.isFinite)) return;
      bounds.push(point);

    });
    if (data?.geometry) {
      const route = L.geoJSON({
        type: 'Feature',
        properties: {},
        geometry: data.geometry
      }, {
        style: {
          color: '#216a96',
          weight: 5
        }
      }).addTo(map);
      if (route.getBounds().isValid()) map.fitBounds(route.getBounds(), {
        animate:false,
        padding: [25, 25]
      });
    } else if (bounds.length > 1) map.fitBounds(bounds, {
      animate:false,
        padding: [25, 25],
      maxZoom: 15
    });
    mapInstance.current = map;setLiveMap(map); setMapZoom(map.getZoom());
    map.on('zoomend', () => setMapZoom(map.getZoom()));
    const timer = setTimeout(() => map.invalidateSize(), 50);
    return () => {
      clearTimeout(timer);
      map.remove(); mapInstance.current = null;setLiveMap(null);
    };
  }, [open, data, mapHome]);
  if (!open) return null;
  return createPortal(<div className="discovery-overlay"><MapPlaceLayers initialLayer={initialLayer} map={liveMap} listingKey={listingKey} controlRef={placeLayersRef} onState={onState} /><section ref={dialogRef} tabIndex={-1} className="discovery-dialog" role="dialog" aria-modal="true" data-assistant-surface="nearby" aria-label="Around this home"><button className="discovery-close" aria-label="Back to property details" onClick={close}>×</button><h2>Around this home</h2>{mapHome && <p>{homeAddress(mapHome)}</p>}<div className="discovery-actions">{['parks', 'groceries', 'coffee', 'trails'].map(category => <button key={category} disabled={busy} aria-pressed={data?.category===category} onClick={() => performLookup('nearby', {category})}>{category[0].toUpperCase() + category.slice(1)}</button>)}</div><form onSubmit={async e => {
        e.preventDefault();
        await performLookup('route');
      }}><label>Travel to an address or destination<input disabled={busy} aria-label="Travel destination" maxLength={300} autoComplete="off" value={destination} onChange={e => {
            setDestination(e.target.value);
            setSelectedDestination(null);setDestinationEditing(true);setError('');
            setData(current=>current?.geometry || current?.choices ? null : current);
          }} placeholder="Work address, park, or other destination" /></label>{suggesting && <p role="status">Finding addresses…</p>}{suggestionError && <p role="status">{suggestionError}</p>}{destinationChoices.length>0 && <ul className="destination-suggestions" aria-label="Destination suggestions">{destinationChoices.map((choice,i)=><li key={choice.id || i}><button type="button" onClick={()=>chooseDestination(choice)}>{choice.address}</button></li>)}</ul>}<label>Travel mode<select disabled={busy} value={mode} onChange={e => {
            setMode(e.target.value);
            setData(current=>current?.geometry ? null : current);
          }}><option value="drive">Driving</option><option value="walk">Walking</option></select></label><button disabled={busy || destination.trim().length<3}>Find travel time</button></form>{busy && <p role="status">Looking up map information…</p>}{error && <p role="alert">{error}</p>}{mapHome && <div ref={mapElement} className="discovery-map" aria-label="Map around the selected home" />}{data?.choices && <ol>{data.choices.map((p, i) => <li key={p.id || i}><button disabled={busy} onClick={() => performLookup('route', {choice:p})}>{i + 1}. {p.address}</button></li>)}</ol>}{data?.places && <div className="discovery-place-summary"><p role="status">{data.places.length ? `${data.places.length} nearby ${data.category} found. ${data.scope || 'Within about 5 miles of this home'}.` : `No nearby ${data.category} were returned. ${data.scope || 'Within about 5 miles of this home'}.`} Zoom or move the map, then search this area for more places.</p><button type="button" disabled={busy || !liveMap} onClick={async()=>{const result=await placeLayersRef.current?.run({command:'search_map_area'});if(result)onResult?.(result);}}>Search this area</button></div>}{data?.places && <ol className="discovery-places">{data.places.map((p, i) => <li key={p.id || i}><strong>{p.name}</strong><span>{p.address}</span><button disabled={busy} onClick={() => performLookup('route', {destination:p.address})}>Travel here</button></li>)}</ol>}{data?.seconds != null && <p className="discovery-answer">About <strong>{Math.round(data.seconds / 60)} minutes</strong> {data.mode === 'walk' ? 'walking' : 'driving'} · {(data.meters / 1609.344).toFixed(1)} miles</p>}{data?.note && <small>{data.note}</small>}</section></div>, document.body);
}
